import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import useDebounce from '../utils/useDebounce';
import { UserKeys, getIndexedGroupsForAutocomplete } from '../api/users-api';
import AvatarChip from './avatar-chip';

import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Group from '@mui/icons-material/Group';

interface GroupOption {
  email: string;
  directMembersCount?: number;
}

interface GroupsBigQueryMultiAutocompleteProps {
  selectedEmails: string[];
  onChange: (emails: string[]) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
  label?: string;
}

// Multi-select sibling of GroupsBigQueryAutocomplete, kept as its own component rather than a
// `multiple` mode bolted onto that one: the single-select version's `value` can be a bare string
// (its getOptionLabel falls back to looking the email up in the latest search results, or '' if
// not found), which is fine for a single value but breaks down for a list of selections that must
// stay valid across search terms - MUI's Autocomplete `value` in multiple mode must be an array of
// full option objects, not strings, so this component always resolves selectedEmails to
// {email, directMembersCount} pairs (falling back to just the email when a selection isn't in the
// current search results) rather than reusing the single-select component's string-passthrough
// path. Shares the exact same debounced BigQuery-backed query as the single-select version.
export default function GroupsBigQueryMultiAutocomplete({
  selectedEmails,
  onChange,
  size,
  disabled,
  label,
}: GroupsBigQueryMultiAutocompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const debouncedInput = useDebounce(inputValue, 500);

  const { data: groupData, isFetching: groupsLoading } = useQuery({
    queryKey: [UserKeys.GroupsAutocomplete, { debouncedInput }],
    queryFn: () =>
      getIndexedGroupsForAutocomplete(
        {
          results: [],
          pageSize: 5,
          currentPage: 0,
          totalCount: 0,
        },
        debouncedInput
      ),
    enabled: debouncedInput !== '',
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
  });

  const options: GroupOption[] = useMemo(
    () =>
      groupData?.results
        .filter((result) => !selectedEmails.includes(result.email))
        .map((result) => ({
          email: result.email,
          directMembersCount: result.directMembersCount,
        })) ?? [],
    [groupData, selectedEmails]
  );

  const selectedOptions: GroupOption[] = useMemo(
    () => selectedEmails.map((email) => ({ email })),
    [selectedEmails]
  );

  return (
    <Autocomplete
      multiple
      id="groups-bigquery-multi-autocomplete"
      disabled={disabled}
      filterOptions={(x) => x}
      options={options}
      size={size}
      value={selectedOptions}
      limitTags={1}
      renderValue={(selected, getItemProps) =>
        selected.map((option, index) => {
          const { key, onDelete } = getItemProps({ index });
          return (
            <AvatarChip
              key={key}
              type="Group"
              email={option.email}
              onDelete={() => onDelete(undefined)}
              disabled={disabled}
            />
          );
        })
      }
      sx={{
        '&& .MuiInputBase-root': {
          height: 'auto',
          minHeight: 40,
          maxHeight: 128,
          overflowY: 'auto',
          marginTop: '0 !important',
          gap: '2px',
        },
        '& .MuiChip-root': { maxWidth: '100%' },
        '&:not(.Mui-focused) .MuiChip-root': { maxWidth: 'calc(100% - 70px)' },
      }}
      componentsProps={{
        paper: {
          sx: {
            minWidth: 400,
          },
        },
      }}
      autoComplete
      autoHighlight
      includeInputInList
      noOptionsText={
        debouncedInput === ''
          ? 'Start typing to search...'
          : 'No results found.'
      }
      getOptionLabel={(option) => option.email}
      isOptionEqualToValue={(option, value) => option.email === value.email}
      onChange={(_event, newValue) => {
        onChange(newValue.map((option) => option.email));
      }}
      renderOption={(props, option: GroupOption) => {
        const { key, ...optionProps } = props;
        return (
          <ListItem key={key} {...optionProps} dense>
            <ListItemAvatar>
              <Avatar>
                <Group />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              id={option.email}
              primary={option.email}
              secondary={`${option.directMembersCount ?? 0} members`}
            />
          </ListItem>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || 'Search for a group'}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {groupsLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
    />
  );
}
